/**
 * Patient Edit Types
 *
 * Type definitions for Patient Edit screen (患者編集)
 * Converted from RA2 Laravel/Blade system
 */

import { z } from 'zod';
import {
  AGE_ELDERLY_CERTIFICATE_ERROR,
  ELDERLY_CERT_TEN_PERCENT_AFTER_HEISEI26_RULE_ERROR,
  ELDERLY_CERT_THIRTY_PERCENT_BEFORE_HEISEI18_ERROR,
  ELDERLY_CERT_TWENTY_PERCENT_QUAL_DATE_EXCLUDED_ERROR,
  HEALTH_INSURANCE_CARD_SYMBOL_REQUIRED,
  HEALTH_INSURER_KIND_NAME_REQUIRED,
  HEALTH_INSURER_NAME_REQUIRED,
  HEALTH_INSURER_NUMBER_REQUIRED,
  LATE_ELDERLY_INSURER_NUMBER_ERROR,
  LATE_ELDERLY_INSURER_NUMBER_LENGTH_ERROR,
  LATE_ELDERLY_RELATIONSHIP_ERROR,
  MEDICAL_AID_APPLICATION_START_DATE_REQUIRED,
  MEDICAL_AID_NUMBER_REQUIRED,
  MEDICAL_AID_PUBLIC_RECIPIENT_NUMBER_REQUIRED,
  PATIENT_ADDRESS_KANA_FORMAT_ERROR,
  RELATIONSHIP_67_INSURER_NUMBER_ERROR,
} from '../constants/validationMessages';

// ==================== Enums and Constants ====================

export interface MasterDataOption {
  id: number;
  name: string;
}

export interface ValuedMasterDataOption extends MasterDataOption {
  value: string;
}

/**
 * 受診者 氏名カナ (patient_informations.name_kana): max 64 (clamp), validate on submit.
 * Matches the Shinsa sync target column t_recept_base.CONSULT_KANA varchar(64).
 */
export const PATIENT_NAME_KANA_MAX_LENGTH = 64;

/**
 * 受診者 氏名漢字 (patient_informations.name): max 64, enforce via UI input cap.
 * Matches t_recept_base.CONSULT_NAME varchar(64) on the Shinsa side.
 */
export const PATIENT_INFO_NAME_MAX_LENGTH = 64;

/** Trim leading/trailing Unicode whitespace (includes U+3000 全角スペース). */
export function trimPatientNameKanaInput(val: string): string {
  return val.replace(/^[\p{White_Space}]+|[\p{White_Space}]+$/gu, '');
}

/**
 * Katakana ァ-ヶ (U+30A1–U+30F6), long vowel ー (U+30FC), half-width ｦ-ﾟ (U+FF66–U+FF9F),
 * spaces: half-width U+0020 and full-width U+3000 only.
 */
export const PATIENT_NAME_KANA_ALLOWED_REGEX = /^[\u30A1-\u30F6\u30FC\uFF66-\uFF9F\u3000 ]+$/u;

/** 受診者 建物・部屋 (address3): max 255, clamp in schema + maxLength on input */
export const PATIENT_ADDRESS3_MAX_LENGTH = 255;

/**
 * 住所1 — shared cap for 受診者 (patient_informations.address1) and 被保険者
 * (health_insurance_cards.address1): max 100, enforce via UI input cap.
 * Matches the Shinsa sync target columns t_recept_base.CONSULT_ADDR_1 / INSUR_ADDR_1 varchar(100).
 */
export const PATIENT_ADDRESS1_MAX_LENGTH = 100;

/** 受診者 住所2: max 255, enforce via UI input cap */
export const PATIENT_ADDRESS2_MAX_LENGTH = 255;

/** 受診者 名称 (address_name): max 255, clamp in schema + maxLength on input */
export const PATIENT_ADDRESS_NAME_MAX_LENGTH = 255;

/** 受診者 住所カナ: max 255, clamp in schema + maxLength on input */
export const PATIENT_ADDRESS_KANA_MAX_LENGTH = 255;
export const PATIENT_ADDRESS_KANA_ALLOWED_REGEX =
  /^[\u3041-\u3096\u30A1-\u30F6\u30FC\uFF66-\uFF9F\uFF10-\uFF19\uFF21-\uFF3A\u3000 （）、?・]+$/u;

/** 受診者 電話番号: max 13 characters (digits + hyphens), UI + schema clamp */
export const PATIENT_TELEPHONE_MAX_LENGTH = 13;

/**
 * 被保険者 / 組合員世帯主 (health_insurance_cards.name): max 64 characters, enforce via UI input cap.
 * Matches t_recept_base.INSUR_NAME varchar(64) on the Shinsa side.
 */
export const HEALTH_INSURANCE_CARD_NAME_MAX_LENGTH = 64;

/**
 * 被保険者名カナ (health_insurance_cards.name_kana): kept at 255 — the column has no Shinsa
 * sync counterpart, so it is out of scope for the 64-character clamp.
 */
export const HEALTH_INSURANCE_CARD_NAME_KANA_MAX_LENGTH = 255;

/**
 * 保険証 記号 (health_insurance_cards.card_mark): max 64 characters, enforce via UI input cap.
 * Matches the Shinsa sync target column t_recept_base.INS_KIGOU varchar(64).
 */
export const HEALTH_INSURANCE_CARD_MARK_MAX_LENGTH = 64;

/**
 * 保険証 番号 / 被保険者番号 (health_insurance_cards.card_number): max 64 characters,
 * enforce via UI input cap. Matches t_recept_base.INS_NUMB varchar(64) on the Shinsa side.
 */
export const HEALTH_INSURANCE_CARD_NUMBER_MAX_LENGTH = 64;

// ==================== Interfaces ====================

/**
 * Patient Information (受診者情報)
 */
export interface PatientInformation {
  id?: number;
  patient_id?: number;
  patient_information_id?: number | null;
  sub_number: number | null;
  name: string;
  name_kana: string;
  sex_id: number | null;
  date_of_birth: string;
  age?: number | null;
  postal_code1: string;
  postal_code2: string;
  address1: string;
  address2: string;
  address3: string;
  address_name: string;
  address_kana: string;
  telephone: string;
  summary: string;
  remarks: string;
  is_exclusion: boolean;
  is_non_output_telephone: boolean;
  gaiji_check: boolean;
  address2_hidden: boolean;
  is_facility_resident: boolean;
  address3_check: boolean;
  is_special_area_addition: boolean;
  available_from?: string | null;
  available_to?: string | null;
  is_current: boolean;
}

/**
 * Health Insurance Card Information (保険証情報)
 */
export interface HealthInsuranceCard {
  id?: number;
  patient_id?: number;
  health_insurance_card_id?: number | null;
  sub_number: number | null;
  relationship_id: number | null;
  insurance_type_id: number | null;
  insurer_id: number | null;
  insurer_number: string;
  insurer_name: string;
  insurer_kind_name: string;
  patient_cost_rate: number | string;
  patient_cost_id?: number | null;
  elderly_insurance_certificate_id?: number | null;
  card_mark: string;
  card_number: string;
  qualification_date: string;
  expiration_date?: string | null;
  name: string;
  name_kana: string;
  sex_id?: number | null;
  date_of_birth?: string | null;
  postal_code1: string;
  postal_code2: string;
  address1: string;
  address2: string;
  address3?: string | null;
  address_kana: string;
  telephone: string;
  claim_type_id?: number | null;
  cost_rate?: string | null;
  family_cost_rate?: string | null;
  is_current: boolean;
}

/**
 * Medical Aid Information (医療助成情報)
 */
export interface MedicalAid {
  id?: number;
  patient_id?: number;
  medical_aid_id?: number | null;
  sub_number: number | null;
  insurer_type_id: number | null;
  insurer_id: number | null;
  /** Resolved from insurers.insurer_name when API loads medicalAids.insurer */
  insurer_name?: string | null;
  medical_aid_number: string;
  medical_aid_patient_number: string;
  insurer_cost_rate: number | string;
  is_partial_aid: boolean;
  is_special: boolean;
  is_nonoutput_rezept?: boolean;
  is_end_medical_aid: boolean;
  calc_type?: number | null;
  limit_amount?: number | null;
  limit_num_month?: number | null;
  limit_amount_month?: number | null;
  claim_type_id?: number | null;
  application_start_date?: string | null;
  application_end_date?: string | null;
  is_current: boolean;
}

/**
 * Main Patient Data
 */
export interface PatientData {
  patient_id: number | null;
  serial_number: string;
  official: boolean;
  visit_last_day: string | null;
  s_keyword: string;
}

/**
 * Complete Patient Form Data
 */
export interface PatientFormData {
  id: number;
  clinic_id: number;
  serial_number: number;
  official: boolean;
  electric_treatment_is?: number | string | null;
  hot_treatment_is?: number | string | null;
  suddenly_visit_is?: number | string | null;
  insurance_type_id?: number | string | null;
  current_patient_information: PatientInformation;
  patient_informations: PatientInformation[];
  health_insurance_cards: HealthInsuranceCard[];
  medical_aids: MedicalAid[];
  patient?: PatientData; // Legacy compatibility
  patient_information?: PatientInformation; // Legacy compatibility
  health_insurance_card?: HealthInsuranceCard; // Legacy compatibility
  medical_aid?: MedicalAid; // Legacy compatibility
  default_address_name?: string | null;
}

/**
 * Patient History Item
 */
export interface PatientHistoryItem {
  id: number;
  date_from: string;
  date_to: string;
  is_current: boolean;
}

/**
 * Lookup Data for dropdowns
 */
export interface PatientLookupData {
  sexs: MasterDataOption[];
  relationships: MasterDataOption[];
  insurance_types: MasterDataOption[];
  patient_costs: ValuedMasterDataOption[];
  elderly_insurance_certificates: ValuedMasterDataOption[];
  insurer_types: MasterDataOption[];
}

// ==================== Zod Schemas for Validation ====================

/**
 * Custom date schema that handles Dayjs objects, Date objects, and strings
 * Includes validation for future dates (DOB cannot be in the future)
 */
const dateSchema = z
  .any()
  .transform(val => {
    if (!val) return '';

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.toDate === 'function') {
      const date = val.toDate();
      return date.toISOString().split('T')[0];
    }

    // Handle Date objects
    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }

    // Handle strings
    if (typeof val === 'string') {
      return val;
    }

    // Return as-is if something else
    return val;
  })
  .refine(val => val !== '', '資格取得日は必須です。')
  .refine(val => {
    // Validate date format
    if (val === '') return true; // Already checked by required validator
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format
    if (!dateRegex.test(val)) return false;
    const date = new Date(val);
    return !isNaN(date.getTime());
  }, '資格取得は正しい日付ではありません。');

/**
 * Custom optional date schema that allows empty strings
 */
const optionalDateSchema = z
  .any()
  .transform(val => {
    if (!val) return '';

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.toDate === 'function') {
      const date = val.toDate();
      return date.toISOString().split('T')[0];
    }

    // Handle Date objects
    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }

    // Handle strings
    if (typeof val === 'string') {
      return val;
    }

    // Return as-is if something else
    return val;
  })
  .refine(val => {
    // Validate date format if value is provided
    if (val === '') return true; // Empty is allowed
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format
    if (!dateRegex.test(val)) return false;
    const date = new Date(val);
    return !isNaN(date.getTime());
  }, '有効期限は正しい日付ではありません。');

/**
 * Optional application start date schema for 医療助成
 * - Empty value is allowed here; required-when-needed is handled in medicalAidSchema refine.
 * - Invalid provided value should show start-date specific message.
 */
const optionalApplicationStartDateSchema = z
  .any()
  .transform(val => {
    if (!val) return '';

    if (typeof val === 'object' && val !== null && typeof val.toDate === 'function') {
      const date = val.toDate();
      return date.toISOString().split('T')[0];
    }

    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }

    if (typeof val === 'string') {
      return val.trim();
    }

    return val;
  })
  .refine(val => {
    if (val === '') return true;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(val)) return false;
    const date = new Date(val);
    return !isNaN(date.getTime());
  }, '適用開始日は正しい日付ではありません。');

const isEmptyDateValue = (value: unknown): boolean => String(value ?? '').trim() === '';

const isDateOnOrAfter = (date: unknown, minDate: unknown): boolean => {
  const dateValue = String(date ?? '').trim();
  const minDateValue = String(minDate ?? '').trim();
  if (dateValue === '' || minDateValue === '') return true;
  return dateValue >= minDateValue;
};

/**
 * Custom date schema for birth dates
 * - Required: "生年月日は必須です。"
 * - Invalid format: "生年月日は正しい日付ではありません。"
 * - Future date (entered manually): produces specific error message that should be shown as a popup
 *   "入力された生年月日に誤りがあります。確認後、訂正して登録してください。"
 */
const birthDateSchema = z
  .any()
  .transform(val => {
    if (!val) return '';

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.toDate === 'function') {
      const date = val.toDate();
      return date.toISOString().split('T')[0];
    }

    // Handle Date objects
    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }

    // Handle strings (pass through)
    if (typeof val === 'string') {
      return val;
    }

    return val;
  })
  .refine(val => val !== '', '生年月日は必須です。')
  .refine(val => {
    if (val === '') return true; // required handled above
    // Accept ISO format YYYY-MM-DD only for manual entry validation here
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (!iso.test(val)) return false;

    const [, y, m, d] = val.match(iso) as RegExpMatchArray;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    const dt = new Date(year, month - 1, day);
    return dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
  }, '生年月日は正しい日付ではありません。')
  .refine(val => {
    if (val === '') return true;
    const [, y, m, d] = (val as string).match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
    if (!y) return true; // format error handled above
    const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    const today = new Date();
    // If DOB is in the future, return false — caller should display popup
    return dt <= new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, '入力された生年月日に誤りがあります。確認後、訂正して登録してください。');

/**
 * Optional birth date schema (allows empty string).
 * Use for 保険証 (health insurance card) 生年月日 where the field is not required.
 * When provided, validates format and rejects future dates with the same messages as birthDateSchema.
 */
const optionalBirthDateSchema = z
  .any()
  .transform(val => {
    if (!val) return '';

    if (typeof val === 'object' && val !== null && typeof val.toDate === 'function') {
      const date = val.toDate();
      return date.toISOString().split('T')[0];
    }
    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }
    if (typeof val === 'string') {
      return val;
    }
    return val;
  })
  .refine(val => {
    if (val === '') return true;
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (!iso.test(val)) return false;
    const [, y, m, d] = val.match(iso) as RegExpMatchArray;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    const dt = new Date(year, month - 1, day);
    return dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
  }, '生年月日は正しい日付ではありません。')
  .refine(val => {
    if (val === '') return true;
    const [, y, m, d] = (val as string).match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
    if (!y) return true;
    const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    const today = new Date();
    return dt <= new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, '入力された生年月日に誤りがあります。確認後、訂正して登録してください。');

export const patientInformationSchema = z.object({
  /** DB row id for the history row being edited (optional in schema; required for API updates). */
  id: z.number().optional(),
  name: z
    .string()
    .transform(val =>
      typeof val === 'string' ? val.trim().slice(0, PATIENT_INFO_NAME_MAX_LENGTH) : val
    )
    .refine(val => val !== '', '氏名漢字は必須です。'),
  name_kana: z
    .string()
    .transform(val =>
      typeof val === 'string'
        ? trimPatientNameKanaInput(val).slice(0, PATIENT_NAME_KANA_MAX_LENGTH)
        : val
    )
    .refine(val => val !== '', '氏名カナは必須です。')
    .refine(val => PATIENT_NAME_KANA_ALLOWED_REGEX.test(val), '氏名カナの書式が正しくありません。'),
  sex_id: z
    .union([z.number(), z.null(), z.undefined()])
    .refine(val => typeof val === 'number' && (val === 1 || val === 2), '性別は必須です。'),
  date_of_birth: birthDateSchema,
  postal_code1: z
    .string()
    .transform(val => {
      if (!val) return '';
      const s = String(val);
      // Convert full-width digits to half-width
      const normalized = s.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
      // Keep digits only and take first 3
      return normalized.replace(/\D/g, '').slice(0, 3);
    })
    .refine(val => val === '' || /^\d{3}$/.test(val), '郵便番号1は3桁にしてください。')
    .optional(),
  postal_code2: z
    .string()
    .transform(val => {
      if (!val) return '';
      const s = String(val);
      // Convert full-width digits to half-width
      const normalized = s.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
      // Keep digits only and take first 4
      return normalized.replace(/\D/g, '').slice(0, 4);
    })
    .refine(val => val === '' || /^\d{4}$/.test(val), '郵便番号2は4桁にしてください。')
    .optional(),
  address1: z
    .string()
    .transform(val => (typeof val === 'string' ? val.trim() : val))
    .refine(val => val !== '', '住所1は必須です。'),
  address2: z
    .string()
    .transform(val => (typeof val === 'string' ? val.trim() : val))
    .refine(val => val !== '', '住所2は必須です。'),
  address3: z
    .string()
    .transform(val => (typeof val === 'string' ? val.slice(0, PATIENT_ADDRESS3_MAX_LENGTH) : val))
    .optional(),
  address_name: z
    .string()
    .transform(val =>
      typeof val === 'string' ? val.trim().slice(0, PATIENT_ADDRESS_NAME_MAX_LENGTH) : val
    )
    .optional(),
  address_kana: z
    .string()
    .transform(val =>
      typeof val === 'string' ? val.slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH) : val
    )
    .refine(
      val => val === '' || PATIENT_ADDRESS_KANA_ALLOWED_REGEX.test(val),
      PATIENT_ADDRESS_KANA_FORMAT_ERROR
    )
    .optional(),
  telephone: z
    .string()
    .transform(val => {
      if (!val) return '';
      const s = String(val);
      const normalized = s.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
      return normalized.slice(0, PATIENT_TELEPHONE_MAX_LENGTH);
    })
    .refine(
      val =>
        val === '' || (/^\d{7,13}$/.test(val.replace(/-/g, '')) && /^[\d-]{7,13}$/.test(val)),
      '電話番号の書式が正しくありません。'
    )
    .optional(),
  summary: z.string().optional(),
  remarks: z.string().optional(),
  is_exclusion: z.boolean().optional(),
  is_non_output_telephone: z.boolean().optional(),
  gaiji_check: z.boolean().optional(),
  address2_hidden: z.boolean().optional(),
  is_facility_resident: z.boolean().optional(),
  address3_check: z.boolean().optional(),
  is_special_area_addition: z.boolean().optional(),
  /** 受診者情報履歴の有効開始日 (YYYY-MM-DD); legacy rows may be empty. */
  available_from: z.string().optional(),
  /** 受診者情報履歴の有効終了日 (YYYY-MM-DD); empty = open end. */
  available_to: z.string().optional(),
});

export const healthInsuranceCardSchema = z
  .object({
    /** DB row id for the history row being edited (optional in schema; required for API updates). */
    id: z.number().optional(),
    relationship_id: z
      .number()
      .nullable()
      .refine(val => val !== null && val >= 1, '続柄は必須です。'),
    insurance_type_id: z.number().min(1, '保険種別は必須です。'),
    insurer_id: z.number().nullable().optional(),
    insurer_number: z.string().transform(val => (typeof val === 'string' ? val.trim() : val)),
    insurer_name: z
      .string()
      .refine(val => val !== '', '保険者名称は必須です。')
      .optional(),
    insurer_kind_name: z
      .string()
      .transform(val => (typeof val === 'string' ? val.trim() : val))
      .refine(val => val !== '', '保険者種別は必須です。')
      .optional(),
    patient_cost_rate: z.string().optional(),
    patient_cost_id: z.number().nullable().optional(),
    elderly_insurance_certificate_id: z.number().nullable().optional(),
    card_mark: z
      .string()
      .transform(val => (typeof val === 'string' ? val.trim() : val))
      .refine(
        val => val === '' || val.length <= HEALTH_INSURANCE_CARD_MARK_MAX_LENGTH,
        '記号は64文字以下にしてください。'
      )
      .optional(),
    card_number: z
      .string()
      .refine(
        val => val === '' || val.length <= HEALTH_INSURANCE_CARD_NUMBER_MAX_LENGTH,
        '番号は64文字以下にしてください。'
      )
      .optional(),
    qualification_date: dateSchema,
    expiration_date: optionalDateSchema,
    name: z
      .string()
      .transform(val => (typeof val === 'string' ? val.trim() : val))
      .refine(val => val !== '', '被保険者は必須です。')
      .refine(
        val => val.length <= HEALTH_INSURANCE_CARD_NAME_MAX_LENGTH,
        '被保険者は64文字以下にしてください。'
      ),
    name_kana: z
      .preprocess(
        val => (val === null || val === undefined ? '' : val),
        z
          .string()
          .refine(
            val => val === '' || PATIENT_NAME_KANA_ALLOWED_REGEX.test(val),
            '被保険者名カナの書式が正しくありません。'
          )
      )
      .optional(),
    sex_id: z.number().optional(),
    date_of_birth: optionalBirthDateSchema,
    postal_code1: z
      .string()
      .transform(val => {
        if (!val) return '';
        const s = String(val);
        const normalized = s.replace(/[０-９]/g, c =>
          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
        );
        return normalized.replace(/\D/g, '').slice(0, 3);
      })
      .refine(val => val === '' || /^\d{3}$/.test(val), '郵便番号1は3桁にしてください。')
      .optional(),
    postal_code2: z
      .string()
      .transform(val => {
        if (!val) return '';
        const s = String(val);
        const normalized = s.replace(/[０-９]/g, c =>
          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
        );
        return normalized.replace(/\D/g, '').slice(0, 4);
      })
      .refine(val => val === '' || /^\d{4}$/.test(val), '郵便番号2は4桁にしてください。')
      .optional(),
    address1: z.string().min(1, '住所1は必須です。'),
    address2: z.string().optional(),
    address_kana: z
      .string()
      .transform(val =>
        typeof val === 'string' ? val.slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH) : val
      )
      .refine(
        val => val === '' || PATIENT_ADDRESS_KANA_ALLOWED_REGEX.test(val),
        PATIENT_ADDRESS_KANA_FORMAT_ERROR
      )
      .optional(),
    telephone: z
      .string()
      .transform(val => {
        if (!val) return '';
        const s = String(val);
        // Convert full-width digits to half-width
        const normalized = s.replace(/[０-９]/g, c =>
          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
        );
        return normalized;
      })
      .refine(
        val =>
          val === '' || (/^\d{7,13}$/.test(val.replace(/-/g, '')) && /^[\d-]{7,13}$/.test(val)),
        '電話番号の書式が正しくありません。'
      )
      .optional(),
    claim_type_id: z.number().nullable().optional(),
    cost_rate: z.number().nullable().optional(),
    family_cost_rate: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // 保険者番号: required except when 生活保護 (type 3) where field is disabled
    if (data.insurance_type_id !== 3) {
      if (!data.insurer_number || data.insurer_number.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: HEALTH_INSURER_NUMBER_REQUIRED,
          path: ['insurer_number'],
        });
      }
    }

    // 記号: required except when 後期高齢 (type 2) or 生活保護 (type 3) where field is disabled.
    // For 一般 (type 1), parity with A2 PatientController "No410": 記号 and 番号 are
    // effectively at-least-one — if 番号 has a value, 記号 may be left blank. 自由診療 (4)
    // keeps 記号 strictly required (A2's conditional override only targets type 1).
    if (data.insurance_type_id !== 2 && data.insurance_type_id !== 3) {
      const cardMarkEmpty = !data.card_mark || data.card_mark.trim() === '';
      const cardNumberFilled = !!data.card_number && data.card_number.trim() !== '';
      const relaxedByCardNumber = data.insurance_type_id === 1 && cardNumberFilled;
      if (cardMarkEmpty && !relaxedByCardNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: HEALTH_INSURANCE_CARD_SYMBOL_REQUIRED,
          path: ['card_mark'],
        });
      }
    }

    if (
      !isEmptyDateValue(data.expiration_date) &&
      !isDateOnOrAfter(data.expiration_date, data.qualification_date)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '有効期限日は、資格取得日以降の日付にしてください。',
        path: ['expiration_date'],
      });
    }
  });

export const medicalAidSchema = z
  .object({
    /** DB row id for the history row being edited (optional in schema; required for API updates). */
    id: z.number().optional(),
    insurer_type_id: z.number().min(0, '医療助成種別は必須です。'),
    insurer_id: z.number().nullable().optional(),
    medical_aid_number: z.string().optional(),
    medical_aid_patient_number: z.string().optional(),
    insurer_cost_rate: z.string().optional(),
    // A2 parity: raw insurer rates that back 医療助成負担 — kept so insurer_cost_rate can be
    // re-derived on 続柄 change (mirrors A2's #cost_rate / #family_cost_rate hidden inputs).
    cost_rate: z.string().optional(),
    family_cost_rate: z.string().optional(),
    is_partial_aid: z.boolean().optional(),
    is_special: z.boolean().optional(),
    calc_type: z.number().nullable().optional(),
    limit_amount: z.number().nullable().optional(),
    limit_num_month: z.number().nullable().optional(),
    limit_amount_month: z.number().nullable().optional(),
    claim_type_id: z.number().nullable().optional(),
    application_start_date: optionalApplicationStartDateSchema,
    application_end_date: optionalDateSchema,
    is_end_medical_aid: z.boolean().optional(),
  })
  .refine(
    data => {
      // If insurer_type_id is "なし" (0), allow empty values
      if (data.insurer_type_id === 0) {
        return true;
      }
      // If other types are selected, medical_aid_number (13.3) is required
      return data.medical_aid_number && data.medical_aid_number.trim() !== '';
    },
    {
      message: MEDICAL_AID_NUMBER_REQUIRED,
      path: ['medical_aid_number'],
    }
  )
  .refine(
    data => {
      // If insurer_type_id is "なし" (0), allow empty values
      if (data.insurer_type_id === 0) {
        return true;
      }
      // If other types are selected, medical_aid_patient_number (13.4) is required
      return data.medical_aid_patient_number && data.medical_aid_patient_number.trim() !== '';
    },
    {
      message: MEDICAL_AID_PUBLIC_RECIPIENT_NUMBER_REQUIRED,
      path: ['medical_aid_patient_number'],
    }
  )
  .refine(
    data => {
      // If insurer_type_id is "なし" (0), allow empty dates
      if (data.insurer_type_id === 0) {
        return true;
      }
      // If other types are selected, application_start_date (13.7) is required
      return String(data.application_start_date || '').trim() !== '';
    },
    {
      message: MEDICAL_AID_APPLICATION_START_DATE_REQUIRED,
      path: ['application_start_date'],
    }
  )
  .refine(
    data => isDateOnOrAfter(data.application_end_date, data.application_start_date),
    {
      message: '適用終了日は、適用開始日以降の日付にしてください。',
      path: ['application_end_date'],
    }
  );

export const patientFormSchema = z.object({
  patient: z
    .object({
      serial_number: z.string().optional(),
      official: z.boolean().optional(),
    })
    .optional(),
  patient_information: patientInformationSchema,
  health_insurance_card: healthInsuranceCardSchema,
  medical_aid: medicalAidSchema,
  /** Internal: まとめて履歴作成 modal → 登録 (A2 inputAvailable / bulk-create options). */
  _bulkHistoryAvailableToMinus: z.string().optional(),
  _bulkHistoryQualificationDate: z.string().optional(),
});

export type PatientFormSchemaType = z.infer<typeof patientFormSchema>;

// ==================== Default Values ====================

export const defaultPatientInformation: PatientInformation = {
  patient_information_id: null,
  sub_number: null,
  name: '',
  name_kana: '',
  sex_id: null,
  date_of_birth: '',
  age: null,
  postal_code1: '',
  postal_code2: '',
  address1: '',
  address2: '',
  address3: '',
  address_name: '',
  address_kana: '',
  telephone: '',
  summary: '',
  remarks: '',
  is_exclusion: false,
  is_non_output_telephone: false,
  gaiji_check: false,
  address2_hidden: false,
  is_facility_resident: false,
  address3_check: false,
  is_special_area_addition: false,
  available_to: '',
  is_current: true,
};

export const defaultHealthInsuranceCard: HealthInsuranceCard = {
  health_insurance_card_id: null,
  sub_number: null,
  relationship_id: null,
  insurance_type_id: 1, // Default to 一般 (General)
  insurer_id: null,
  insurer_number: '',
  insurer_name: '',
  insurer_kind_name: '',
  patient_cost_rate: '',
  patient_cost_id: null,
  elderly_insurance_certificate_id: null,
  card_mark: '',
  card_number: '',
  qualification_date: '',
  expiration_date: '',
  name: '',
  name_kana: '',
  sex_id: null,
  date_of_birth: '',
  postal_code1: '',
  postal_code2: '',
  address1: '',
  address2: '',
  address_kana: '',
  telephone: '',
  claim_type_id: null,
  cost_rate: '',
  family_cost_rate: '',
  is_current: true,
};

export const defaultMedicalAid: MedicalAid = {
  medical_aid_id: null,
  sub_number: null,
  insurer_type_id: 0, // なし
  insurer_id: null,
  insurer_name: '',
  medical_aid_number: '',
  medical_aid_patient_number: '',
  insurer_cost_rate: '',
  is_partial_aid: false,
  is_special: false,
  calc_type: null,
  limit_amount: null,
  limit_num_month: null,
  limit_amount_month: null,
  claim_type_id: null,
  application_start_date: '',
  application_end_date: '',
  is_end_medical_aid: false,
  is_current: true,
};

export const defaultPatientData: PatientData = {
  patient_id: null,
  serial_number: '',
  official: false,
  visit_last_day: null,
  s_keyword: '',
};

export const defaultPatientFormData: PatientFormData = {
  id: 0,
  clinic_id: 0,
  serial_number: 0,
  official: false,
  current_patient_information: defaultPatientInformation,
  patient_informations: [],
  health_insurance_cards: [],
  medical_aids: [],
  patient: defaultPatientData,
  patient_information: defaultPatientInformation,
  health_insurance_card: defaultHealthInsuranceCard,
  medical_aid: defaultMedicalAid,
};

// ==================== Custom Validation Functions ====================

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string): number {
  if (!dateOfBirth) return 0;
  const birth = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Validate age-based elderly certificate selection
 * RA2 Rule: No55 - Users under 70 cannot select elderly insurance certificate rates 1割, 2割, 3割
 */
export function validateAgeForElderlyCertificate(
  age: number,
  elderlyInsuranceCertificateId: number | null | undefined
): { isValid: boolean; message?: string } {
  if (
    age > 0 &&
    age < 70 &&
    elderlyInsuranceCertificateId &&
    [2, 3, 4].includes(elderlyInsuranceCertificateId)
  ) {
    return {
      isValid: false,
      message: AGE_ELDERLY_CERTIFICATE_ERROR,
    };
  }
  return { isValid: true };
}

/**
 * Validate insurer number prefix based on relationship
 * RA2 Rules:
 * - No893: 後期高齢 requires relationship = 本人 (1)
 * - No142: 後期高齢 requires insurer number starting with "39"
 * - No899: 67（本人） requires insurer number starting with "67"
 */
export function validateInsurerNumberPrefix(
  relationshipId: number | null,
  insuranceTypeId: number | null,
  insurerNumber: string
): { isValid: boolean; message?: string } {
  if (!insurerNumber || !relationshipId || !insuranceTypeId) {
    return { isValid: true }; // Skip validation if fields are empty
  }

  // Skip validation for 生活保護 (Social Welfare) as it has fixed values
  if (insuranceTypeId === 3) {
    return { isValid: true };
  }

  // Skip validation if insurer number is a fixed value from another type that's in transition
  // 00000001 is the fixed value for 生活保護 during transitions to other types
  if (insurerNumber === '00000001') {
    return { isValid: true };
  }

  // Normalize insurer number: convert full-width digits to half-width
  const normalizedNumber = insurerNumber.replace(/[０-９]/g, char =>
    String.fromCharCode(char.charCodeAt(0) - 0xfee0)
  );

  // 後期高齢 (Elderly) validation - format rules
  if (insuranceTypeId === 2) {
    // Only validate format rules when we have 8+ digits
    if (normalizedNumber.length >= 8 && /^\d{8}/.test(normalizedNumber)) {
      // Must be 本人 (self)
      if (relationshipId !== 1) {
        return {
          isValid: false,
          message: LATE_ELDERLY_RELATIONSHIP_ERROR,
        };
      }
      // Must start with "39"
      if (!normalizedNumber.startsWith('39')) {
        return {
          isValid: false,
          message: LATE_ELDERLY_INSURER_NUMBER_ERROR,
        };
      }
    }
  }

  // 67（本人） validation - only when 8 digits are present
  if (relationshipId === 3) {
    if (normalizedNumber.length >= 8 && /^\d{8}/.test(normalizedNumber)) {
      // Must start with "67"
      if (!normalizedNumber.startsWith('67')) {
        return {
          isValid: false,
          message: RELATIONSHIP_67_INSURER_NUMBER_ERROR,
        };
      }
    }
  }

  return { isValid: true };
}

/**
 * Validate card number digits for elderly insurance
 * RA2 Rule: No57 - 後期高齢 requires exactly 8 digits
 * Note: 生活保護 is excluded as it has fixed values
 */
export function validateCardNumberForElderly(
  insuranceTypeId: number | null,
  cardNumber: string,
  officialMode: boolean = true
): { isValid: boolean; message?: string } {
  // Skip validation for 生活保護 (Social Welfare) as it has fixed values
  if (insuranceTypeId === 3) {
    return { isValid: true };
  }

  // Skip validation if card number is a fixed value from another type that's in transition
  // １ is the fixed value for 生活保護 during transitions to other types
  if (cardNumber === '１') {
    return { isValid: true };
  }

  // Only check 8-digit requirement in official mode (on submit)
  if (officialMode && insuranceTypeId === 2) {
    // Convert full-width to half-width numbers
    const normalizedCardNumber = cardNumber.replace(/[０-９]/g, s =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    );

    if (normalizedCardNumber.length !== 8 || !/^[0-9]{8}$/.test(normalizedCardNumber)) {
      return {
        isValid: false,
        message: LATE_ELDERLY_INSURER_NUMBER_LENGTH_ERROR,
      };
    }
  }
  return { isValid: true };
}

/**
 * Validate qualification date based rules
 * Validates qualification date rules for elderly insurance certificates
 *
 * Rules:
 * - 3割 (id=4): Patient must be >=70 at qualification date AND date must be >= 2006/10/01
 * - 2割 (id=3): Patient must be >=70 at qualification date AND date must NOT be between 2006/10/01-2014/03/31
 * - 1割 (id=2): Cannot assign if patient age <70 and qualification date is on or after 2014/04/02
 */
export function validateQualificationDateRules(
  qualificationDate: string | Date | any,
  elderlyInsuranceCertificateId: number | null,
  dateOfBirth: string
): { isValid: boolean; message?: string } {
  if (!qualificationDate || !dateOfBirth) return { isValid: true };

  // Convert qualificationDate to string if it's not already
  let qualDateStr: string;
  if (typeof qualificationDate === 'string') {
    qualDateStr = qualificationDate;
  } else if (qualificationDate instanceof Date) {
    qualDateStr = qualificationDate.toISOString().split('T')[0];
  } else if (
    typeof qualificationDate === 'object' &&
    qualificationDate !== null &&
    typeof qualificationDate.toDate === 'function'
  ) {
    // Handle Dayjs objects
    qualDateStr = qualificationDate.toDate().toISOString().split('T')[0];
  } else {
    return { isValid: true }; // Invalid type, skip validation
  }

  const qualDate = new Date(qualDateStr.replace(/\//g, '-'));

  // Note: Age-based elderly certificate checks (age < 70) are handled separately
  // by validateAgeForElderlyCertificate() using the patient's current age.
  // This function only validates date-based constraints on the qualification date.

  // Rule 1: 3割 (id=4) - date must be >= 2006/10/01
  const heiseiOct01 = new Date('2006-10-01');
  if (elderlyInsuranceCertificateId === 4) {
    if (qualDate < heiseiOct01) {
      return {
        isValid: false,
        message: ELDERLY_CERT_THIRTY_PERCENT_BEFORE_HEISEI18_ERROR,
      };
    }
  }

  // Rule 2: 2割 (id=3) - date must NOT be between 2006/10/01 and 2014/03/31
  const ruleStart = new Date('2006-10-01');
  const ruleEnd = new Date('2014-03-31');
  if (elderlyInsuranceCertificateId === 3) {
    if (qualDate >= ruleStart && qualDate <= ruleEnd) {
      return {
        isValid: false,
        message: ELDERLY_CERT_TWENTY_PERCENT_QUAL_DATE_EXCLUDED_ERROR,
      };
    }
  }

  // Rule 3: 1割 (id=2) - Cannot assign if patient was under 70 as of 2014/04/01 and qualification date is on or after 2014/04/01
  const heisei26Apr01 = new Date('2014-04-01');
  if (elderlyInsuranceCertificateId === 2 && qualDate >= heisei26Apr01) {
    const birthDate = new Date(dateOfBirth);
    const refDate = new Date('2014-04-01');
    const ageAtRef = refDate.getFullYear() - birthDate.getFullYear();
    const refMonthDay = refDate.getMonth() * 100 + refDate.getDate();
    const birthMonthDay = birthDate.getMonth() * 100 + birthDate.getDate();
    const adjustedAge = refMonthDay < birthMonthDay ? ageAtRef - 1 : ageAtRef;
    if (adjustedAge < 70) {
      return {
        isValid: false,
        message: ELDERLY_CERT_TEN_PERCENT_AFTER_HEISEI26_RULE_ERROR,
      };
    }
  }

  return { isValid: true };
}

/**
 * Comprehensive validation function for health insurance card
 */
export function validateHealthInsuranceCard(formData: PatientFormSchemaType): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const healthCard = formData.health_insurance_card;

  // Insurer kind name validation (required) — RA2N-specific extra check
  if (!healthCard.insurer_kind_name || healthCard.insurer_kind_name.trim() === '') {
    errors.push(HEALTH_INSURER_KIND_NAME_REQUIRED);
  }

  // Insurer name validation (required) — RA2N-specific extra check
  if (!healthCard.insurer_name || healthCard.insurer_name.trim() === '') {
    errors.push(HEALTH_INSURER_NAME_REQUIRED);
  }

  // NOTE: Age-based elderly certificate (A2 step 2) and card number for elderly (A2 step 4)
  // are now handled by useCrossSectionValidation.runAllValidations() in A2 order.

  return {
    isValid: errors.length === 0,
    errors,
  };
}
